import React, { useRef, useMemo, useEffect } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls, Text } from '@react-three/drei';
import * as THREE from 'three';

function Face({ position, rotation, squares, onSquareClick, isBotPlaying, gameEnded }) {
  const faceRef = useRef();

  const faceSquares = squares.map((square, i) => (
    <group key={i} position={[
      (i % 3) * 2 - 2,
      2 - Math.floor(i / 3) * 2,
      0.1,
    ]}>
      <mesh onClick={(e) => {
        e.stopPropagation();
        !isBotPlaying && !square && !gameEnded && onSquareClick(i);
      }}>
        <planeGeometry args={[1.9, 1.9]} />
        <meshStandardMaterial color="wheat" transparent opacity={0.5} />
      </mesh>
      {square && (
        <Text
          position={[0, 0, 0.001]}
          fontSize={1}
          fontWeight={"bold"}
          color={square === 'X' ? 'blue' : 'red'}
          anchorX="center"
          anchorY="middle"
        >
          {square}
        </Text>
      )}
    </group>
  ));

  return (
    <group ref={faceRef} position={position} rotation={rotation}>
      {faceSquares}
    </group>
  );
}

function Cube({ currentSquares, handleClick, isBotPlaying, gameEnded }) {
  const faces = currentSquares.map((squares, face) => (
    <Face
      key={face}
      position={getPosition(face)}
      rotation={getRotation(face)}
      squares={squares}
      onSquareClick={(i) => handleClick(i, face)}
      isBotPlaying={isBotPlaying}
      gameEnded={gameEnded}
    />
  ));

  const gradientMaterial = useMemo(() => {
    const vertexShader = `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `;
    
    const fragmentShader = `
      varying vec2 vUv;
      void main() {
        gl_FragColor = vec4(vUv, 1.0, 1.0);
      }
    `;
    
    return new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      side: THREE.DoubleSide,
    });
  }, []);

  return (
    <group >
      <mesh material={gradientMaterial}>
        <boxGeometry args={[6, 6, 6]} />
      </mesh>
      {faces}
    </group>
  );
}

function getPosition(face) {
  switch (face) {
    case 0:
      return [0, 0, 3];
    case 1:
      return [3, 0, 0];
    case 2:
      return [0, 0, -3];
    case 3:
      return [-3, 0, 0];
    case 4:
      return [0, 3, 0];
    case 5:
      return [0, -3, 0];
    default:
      return [0, 0, 0];
  }
}

function getRotation(face) {
  switch (face) {
    case 0:
      return [0, 0, 0];
    case 1:
      return [0, Math.PI / 2, 0];
    case 2:
      return [0, Math.PI, 0];
    case 3:
      return [0, -Math.PI / 2, 0];
    case 4:
      return [-Math.PI / 2, 0, 0];
    case 5:
      return [Math.PI / 2, 0, 0];
    default:
      return [0, 0, 0];
  }
}

function CameraControls() {
    const { camera } = useThree();
    const controls = useRef();
  
    useEffect(() => {
      if (controls.current) {
        controls.current.target.set(0, 0, 0);
        camera.position.set(8.5, 8.5, 8.5);
        controls.current.update();
      }
    }, [camera]);
  
    return <OrbitControls ref={controls} makeDefault minDistance={8.5} maxDistance={8.5} />;
}

export default function ThreeDTicTacToe({ history, handlePlay, currentMove, isBotPlaying, gameEnded }) {
  const currentSquares = history[currentMove];

  const handleClick = (i, face) => {
    if (!currentSquares[face][i]) {
      const nextSquares = currentSquares.map((faceSquares, idx) =>
        idx === face
          ? faceSquares.map((square, j) => (j === i ? (currentMove % 2 === 0 ? 'X' : 'O') : square))
          : faceSquares
      );
      handlePlay(nextSquares);
    }
  };

  return (
    <Canvas style={{height:"50vh", width:"25vw"}}>
      <ambientLight />
      <pointLight position={[10, 10, 10]} />
      <CameraControls />
      <Cube currentSquares={currentSquares} handleClick={handleClick} isBotPlaying={isBotPlaying} gameEnded={gameEnded} />
    </Canvas>
  );
}
